import { trackPageView } from '@snowplow/browser-tracker';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Switch, Route, useHistory, Redirect, useLocation, matchPath } from 'react-router-dom';

import { usePermissionsContext } from '@/app/contexts/permissions';
import { useAppAuth } from '@/modules/auth/hooks/useAppAuth';
import { startPressurePolling, stopPressurePolling } from '@/modules/common/store/actions';
import { loadInitialData } from '@/modules/partner/actions/async/loadInitialData';
import {
  ToDo,
  Overview,
  Bills,
  Profile,
  ProfilePayments,
  ProfilePayouts,
  ProfileContactInformation,
  ProfileChauffeur,
  Journeys,
  Journey,
  Vehicles,
  Vehicle,
  VehicleEdit,
  Chauffeurs,
  Chauffeur,
  ChauffeurEditInfo,
  ChauffeurEditVehicle,
  AddVehicle,
  AddChauffeur,
  TypeFormRedirect,
  Auth,
  Wizard,
} from '@/pages';
import { AppDispatch } from '@/root-store';
import { IHistory } from '@/services/HistoryExtended';
import { ErrorBySlug, ErrorWrapper } from '@/shared/view/Error';
import { MainLayout } from '@/shared/view/MainLayout';
import { ISwapTransitionRef, SwapTransition } from '@/shared/view/SwapTransition';

import s from './style.module.css';

const match = (
  config: Record<string, (params: Record<string, string>) => React.ReactNode>,
  notFoundRender: () => React.ReactNode,
  currentPath: string,
) => {
  for (const pattern in config) {
    const result = matchPath(currentPath, {
      path: pattern,
      exact: true,
    });

    if (result) {
      return config[pattern](result.params);
    }
  }

  return notFoundRender();
};

const MainRoutes = () => {
  const { isAuthenticated } = useAppAuth();
  const dispatch: AppDispatch = useDispatch();
  const acl = usePermissionsContext();

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(loadInitialData());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && acl.can.viewPressure) {
      dispatch(startPressurePolling());
    }

    return () => {
      dispatch(stopPressurePolling());
    };
  }, [acl.can.viewPressure, dispatch, isAuthenticated]);

  const transitionTargetElementRef = useRef<HTMLDivElement>(null);
  const controlTransitionRef = useRef<ISwapTransitionRef>(null);

  const location = useLocation();
  const history = useHistory() as IHistory;

  const onNavigate = useCallback(
    async (uri: string, replace = false) => {
      if (replace) {
        history.replace(uri);
      } else {
        history.push(uri);
      }
    },
    [history],
  );

  const onNavigateBack = useCallback(
    async (uri: string) => {
      controlTransitionRef.current?.reverseNextTransition();
      history.goBackSmart(uri);
    },
    [history],
  );

  const screens = useMemo(
    () =>
      match(
        {
          '/todo': () => <ToDo onNavigate={onNavigate} />,
          '/bills': () => <Bills />,
          '/overview': () => <Overview />,

          '/profile': () => <Profile onNavigate={onNavigate} onNavigateBack={onNavigateBack} />,
          '/profile/payments': () => (
            <ProfilePayments onGoBack={() => onNavigateBack('/profile')} />
          ),
          '/profile/payouts': () => <ProfilePayouts onGoBack={() => onNavigateBack('/profile')} />,
          '/profile/payouts-company': () => (
            <ProfilePayouts onGoBack={() => onNavigateBack('/profile')} isCompany={true} />
          ),
          '/profile/contact-information': () => (
            <ProfileContactInformation onGoBack={() => onNavigateBack('/profile')} />
          ),
          '/profile/chauffeur': () => <ProfileChauffeur onNavigateBack={onNavigateBack} />,

          '/journeys': () => <Journeys />,
          '/journeys/:journeyId': ({ journeyId }) => (
            <Journey journeyId={journeyId} onNavigateBack={onNavigateBack} />
          ),

          '/add-vehicle': () => (
            <AddVehicle onNavigate={onNavigate} onNavigateBack={onNavigateBack} />
          ),
          '/vehicles': () => <Vehicles onNavigate={onNavigate} />,
          '/vehicles/:carId': ({ carId }) => (
            <Vehicle carId={carId} onNavigateBack={onNavigateBack} />
          ),
          '/vehicles/:carId/edit': ({ carId }) => (
            <VehicleEdit carId={carId} onNavigateBack={onNavigateBack} />
          ),

          '/add-chauffeur': () => (
            <AddChauffeur onNavigate={onNavigate} onNavigateBack={onNavigateBack} />
          ),
          '/chauffeurs': () => <Chauffeurs onNavigate={onNavigate} />,
          '/chauffeurs/:driverId': () => <Chauffeur onNavigateBack={onNavigateBack} />,
          '/chauffeurs/:driverId/edit-info': () => (
            <ChauffeurEditInfo onNavigateBack={onNavigateBack} />
          ),
          '/chauffeurs/:driverId/edit-vehicle': () => (
            <ChauffeurEditVehicle onNavigateBack={onNavigateBack} />
          ),
        },
        () => <ErrorBySlug slug={'404'} />,
        location.pathname,
      ),
    [location.pathname, onNavigate, onNavigateBack],
  );

  const { can, isReady } = usePermissionsContext();

  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  if (!isReady) {
    return null;
  }

  return (
    <Switch>
      <Redirect path="/" exact={true} to={can.viewWizard ? '/join' : '/todo'} />
      <Route path={'/join'} component={Wizard} />
      <Route>
        <SwapTransition
          targetElementRef={transitionTargetElementRef}
          transitionKey={location.pathname}
          ref={controlTransitionRef}
        >
          <MainLayout onNavigate={onNavigate}>
            <div ref={transitionTargetElementRef} className={s.screens}>
              {screens}
            </div>
          </MainLayout>
        </SwapTransition>
      </Route>
    </Switch>
  );
};

const App = React.memo(() => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    trackPageView();
  }, [pathname, search]);

  return (
    <ErrorWrapper>
      <Switch>
        <Route path="/typeform-redirect/:testId" component={TypeFormRedirect} />
        <Route path={['/login', '/register']} component={Auth} />
        <Route component={MainRoutes} />
      </Switch>
    </ErrorWrapper>
  );
});

App.displayName = 'App';

export { App };
